import { UseFormReturn } from 'react-hook-form';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { HighlightableSelect } from '@/components/ui/highlightable-select';
import { AddableSelect } from '@/components/ui/addable-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import {
  MapPin,
  Mail,
  Phone,
  CalendarCheck,
  Plus,
  Trash2,
  CheckCheck,
  Copy,
  ExternalLink,
} from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { EQUIPMENT_OPTIONS } from '@/lib/transformers/site-survey';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ResponsiveImageUpload } from '../shared/responsive-image-upload';
import { DatePicker } from '@/components/ui/date-picker';
import { uploadSiteSurveyPhoto } from '../shared/site-survey-photo-upload';
import { useEquipmentOptions, formatEquipmentOptionsForSelect } from '@/lib/api/equipment-options';
import { SurveyorAvailabilityPopup } from '../surveyor-availability-popup';
import { buildWhat3wordsMapUrl, formatWhat3wordsAddress } from '@shared/what3words';

interface ProjectInfoProps {
  form: UseFormReturn<any>;
  onPhotoUploaded?: () => void;
  installationId?: number;
  existingSurvey?: { id: number } | null;
}

interface SiteSurveyCustomer {
  id: number;
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  propertyType?: string | null;
  what3wordsAddress?: string | null;
}

// UK postcode validation regex
const UK_POSTCODE_REGEX = /^[A-Z]{1,2}[0-9][0-9A-Z]?\s?[0-9][A-Z]{2}$/i;

export function ProjectInfo({
  form,
  onPhotoUploaded,
  installationId: propInstallationId,
  existingSurvey,
}: ProjectInfoProps) {
  const [uploadingImages, setUploadingImages] = useState<Record<string, boolean>>({});
  const [showAvailabilityPopup, setShowAvailabilityPopup] = useState(false);
  const [copiedWhat3wordsValue, setCopiedWhat3wordsValue] = useState<string | null>(null);
  const what3wordsCopyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initialize highlighted fields from form data
  const [highlightedFields, setHighlightedFields] = useState<Record<string, boolean>>(() => {
    const savedHighlights = form.getValues('highlightedFields');
    return savedHighlights || {};
  });
  const { toast } = useToast();

  // Watch panels array for reactive UI
  const panels = form.watch('panels') || [];
  const additionalPanels = panels.slice(1);

  const buildPanelEntries = (
    additionalEntries: any[] = additionalPanels,
    primaryDescription = form.getValues('panelsDescription') || '',
    primaryQuantity = form.getValues('panelsQuantity'),
    primaryHighlighted = form.getValues('highlightedFields')?.panelsDescription || false
  ) => {
    const nextPanels = primaryDescription || primaryQuantity || additionalEntries.length > 0
      ? [{
          id: panels[0]?.id || 'primary',
          description: primaryDescription,
          quantity: primaryQuantity || 0,
          highlighted: primaryHighlighted,
        }]
      : [];

    return [...nextPanels, ...additionalEntries];
  };

  const syncPanelEntries = (
    additionalEntries: any[] = additionalPanels,
    primaryDescription?: string,
    primaryQuantity?: number | null,
    primaryHighlighted?: boolean
  ) => {
    form.setValue('panels', buildPanelEntries(
      additionalEntries,
      primaryDescription,
      primaryQuantity,
      primaryHighlighted
    ), {
      shouldValidate: false,
      shouldDirty: true,
    });
  };

  const addPanel = () => {
    const nextAdditionalPanels = [
      ...additionalPanels,
      {
        id: Date.now().toString(),
        description: '',
        quantity: 1,
        highlighted: false,
      },
    ];
    syncPanelEntries(nextAdditionalPanels);
  };

  const removePanel = (index: number) => {
    const nextAdditionalPanels = additionalPanels.filter((_: any, i: number) => i !== index);
    syncPanelEntries(nextAdditionalPanels);
  };

  const updatePanel = (
    index: number,
    field: 'description' | 'quantity' | 'highlighted',
    value: any
  ) => {
    const updated = [...additionalPanels];
    updated[index] = { ...updated[index], [field]: value };
    syncPanelEntries(updated);
  };

  // Watch batteries array for reactive UI
  const batteries = form.watch('batteries') || [];

  // Helper functions for managing batteries array
  const addBattery = () => {
    const current = form.getValues('batteries') || [];
    form.setValue('batteries', [...current, {
      id: Date.now().toString(),
      description: '',
      quantity: 1,
      highlighted: false,
    }]);
  };

  const removeBattery = (index: number) => {
    const current = form.getValues('batteries') || [];
    form.setValue('batteries', current.filter((_: any, i: number) => i !== index));
  };

  const updateBattery = (index: number, field: 'description' | 'quantity' | 'highlighted', value: any) => {
    const current = form.getValues('batteries') || [];
    const updated = [...current];
    updated[index] = { ...updated[index], [field]: value };
    form.setValue('batteries', updated);
  };

  // Watch for changes to highlightedFields in the form and sync to local state
  const formHighlightedFields = form.watch('highlightedFields');
  useEffect(() => {
    if (formHighlightedFields && Object.keys(formHighlightedFields).length > 0) {
      console.log('[ProjectInfo] Syncing highlightedFields from form:', formHighlightedFields);
      setHighlightedFields(formHighlightedFields);
    }
  }, [formHighlightedFields]);

  // State for panel dimensions (including electrical specs)
  const [panelDimensions, setPanelDimensions] = useState<{
    width?: number;
    length?: number;
    depth?: number;
    unit?: string;
    voc?: number;
    isc?: number;
  }>(() => {
    const savedDimensions = form.getValues('panelDimensions');
    return savedDimensions || { depth: 30, unit: 'mm' };
  });

  // Fetch equipment options from API with session caching
  const { data: equipmentOptions, isLoading: loadingOptions, refetch: refetchOptions } = useEquipmentOptions();
  const fileInputRefs = {
    stringPlanFromOpenSolar: useRef<HTMLInputElement>(null),
    aerialImageFromDrone: useRef<HTMLInputElement>(null),
    generalImage1: useRef<HTMLInputElement>(null),
    generalImage2: useRef<HTMLInputElement>(null),
    generalImage3: useRef<HTMLInputElement>(null),
  };

  // Fetch customer data for auto-population
  const customerId = form.watch('customerId');
  const installationId = propInstallationId || form.watch('installationId');
  const selectedPanelType = panels[0]?.description || form.watch('panelsDescription');

  const { data: customer } = useQuery<SiteSurveyCustomer>({
    queryKey: ['/api/customers', customerId],
    queryFn: async () => {
      const response = await fetch(`/api/customers/${customerId}`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch customer');
      return response.json();
    },
    enabled: !!customerId && customerId > 0,
  });
  const what3wordsDisplayValue = formatWhat3wordsAddress(customer?.what3wordsAddress);
  const what3wordsMapUrl = buildWhat3wordsMapUrl(what3wordsDisplayValue);

  useEffect(() => {
    return () => {
      if (what3wordsCopyTimeoutRef.current) {
        clearTimeout(what3wordsCopyTimeoutRef.current);
      }
    };
  }, []);

  const copyWhat3wordsAddress = async () => {
    if (!what3wordsDisplayValue) return;

    try {
      await navigator.clipboard.writeText(what3wordsDisplayValue);
      setCopiedWhat3wordsValue(what3wordsDisplayValue);
      if (what3wordsCopyTimeoutRef.current) {
        clearTimeout(what3wordsCopyTimeoutRef.current);
      }
      what3wordsCopyTimeoutRef.current = setTimeout(() => {
        setCopiedWhat3wordsValue(null);
      }, 2000);
      toast({
        title: 'Precise location copied',
        description: what3wordsDisplayValue,
      });
    } catch (error) {
      console.error('[ProjectInfo] Failed to copy what3words location:', error);
      toast({
        title: 'Could not copy precise location',
        description: 'Please select and copy the what3words address manually.',
        variant: 'destructive',
      });
    }
  };

  // Fetch installation data for address
  const { data: installation } = useQuery({
    queryKey: [`/api/installations/${installationId}`],
    queryFn: async () => {
      const response = await fetch(`/api/installations/${installationId}`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch installation');
      return response.json();
    },
    enabled: !!installationId && installationId > 0 && installationId !== customerId,
  });


  // Map customer property types to site survey dropdown options
  const mapCustomerPropertyToSurveyType = (customerType: string | null | undefined): string => {
    if (!customerType) return '';
    const mapping: Record<string, string> = {
      'detached': 'HOUSE',
      'detached house': 'HOUSE',
      'semi-detached': 'SEMI',
      'semi-detached house': 'SEMI',
      'terraced': 'TERRACED',
      'terraced house': 'TERRACED',
      'bungalow': 'BUNGALOW',
      'flat': 'FLAT',
      'apartment': 'FLAT',
      'commercial': 'OTHER COMMERCIAL',
      'industrial': 'INDUSTRIAL',
      'agricultural': 'OUTBUILDING',
      'outbuilding': 'OUTBUILDING',
      'offices': 'OFFICES',
    };
    const normalized = customerType.toLowerCase().trim();
    return mapping[normalized] || customerType.toUpperCase();
  };

  // Auto-populate form fields when customer data is loaded
  // Only populate if fields are empty (don't overwrite initial data from parent)
  useEffect(() => {
    if (customer) {
      // Check if fields are already populated from initial data
      const currentValues = form.getValues();

      // Set customer name only if empty
      if (!currentValues.clientName) {
        const fullName =
          `${(customer as any).firstName || ''} ${(customer as any).lastName || ''}`.trim();
        if (fullName) {
          form.setValue('clientName', fullName);
        }
      }

      // Set phone only if empty
      if (!currentValues.clientTelephone && (customer as any).phone) {
        form.setValue('clientTelephone', (customer as any).phone);
      }

      // Set email only if empty
      if (!currentValues.clientEmail && (customer as any).email) {
        form.setValue('clientEmail', (customer as any).email);
      }

      // Parse address to extract components only if address fields are empty
      if (!currentValues.clientAddress && (customer as any).address) {
        const addressParts = parseAddress((customer as any).address);
        form.setValue('clientAddress', addressParts.streetAddress);
        form.setValue('clientCity', addressParts.city);
        form.setValue('clientPostCode', addressParts.postcode);
      }

      // Set property type only if empty (auto-populate from customer lead form data)
      if (!currentValues.propertyType && (customer as any).propertyType) {
        const mappedType = mapCustomerPropertyToSurveyType((customer as any).propertyType);
        if (mappedType) {
          form.setValue('propertyType', mappedType);
        }
      }
    }
  }, [customer, form]);

  // Auto-populate installation address if different from customer address
  // Only populate if fields are empty (don't overwrite initial data from parent)
  useEffect(() => {
    if (installation?.installationAddress) {
      const currentValues = form.getValues();

      // Only update if address fields are empty and installation address is different from customer address
      if (
        !currentValues.clientAddress &&
        installation.installationAddress !== (customer as any)?.address
      ) {
        const addressParts = parseAddress(installation.installationAddress);

        // Only show toast if we're actually updating the address
        if (addressParts.streetAddress || addressParts.city || addressParts.postcode) {
          toast({
            title: 'Using Installation Address',
            description:
              "The survey will be conducted at the installation address, not the customer's billing address.",
            duration: 5000,
          });

          if (addressParts.streetAddress) {
            form.setValue('clientAddress', addressParts.streetAddress);
          }
          if (addressParts.city) {
            form.setValue('clientCity', addressParts.city);
          }
          if (addressParts.postcode) {
            form.setValue('clientPostCode', addressParts.postcode);
          }
        }
      }
    }
  }, [installation, customer, form, toast]);

  // Parse UK address into components
  const parseAddress = (address: string) => {
    if (!address) return { streetAddress: '', city: '', postcode: '' };

    let streetAddress = '';
    let city = '';
    let postcode = '';

    // First, try to extract the postcode (it could be anywhere in the string)
    const postcodeMatch = address.match(UK_POSTCODE_REGEX);
    if (postcodeMatch) {
      postcode = postcodeMatch[0].toUpperCase();
      // Remove the postcode from the address for further parsing
      address = address.replace(postcodeMatch[0], '').trim();
    }

    // Remove any trailing commas or extra spaces
    address = address.replace(/,\s*$/, '').replace(/\s+/g, ' ').trim();

    // Check if the address is just a postcode (common case)
    if (!address && postcode) {
      // If we only have a postcode, try to use installation or customer address
      if (installation?.installationAddress && installation.installationAddress !== postcode) {
        const altAddress = installation.installationAddress
          .replace(postcodeMatch?.[0] || '', '')
          .trim();
        if (altAddress) {
          address = altAddress;
        }
      } else if ((customer as any)?.address && (customer as any).address !== postcode) {
        const altAddress = (customer as any).address.replace(postcodeMatch?.[0] || '', '').trim();
        if (altAddress) {
          address = altAddress;
        }
      }
    }

    // Split by comma to get address parts
    const parts = address
      .split(',')
      .map((line) => line.trim())
      .filter((line) => line);

    if (parts.length === 0) {
      // If no parts after postcode removal, the whole thing might be the street address
      streetAddress = address || '';
    } else if (parts.length === 1) {
      // If only one part, it's likely just the street address
      streetAddress = parts[0];
    } else if (parts.length === 2) {
      // Two parts: street address and city
      streetAddress = parts[0];
      city = parts[1];
    } else {
      // Multiple parts: last is city, rest is street address
      city = parts[parts.length - 1];
      streetAddress = parts.slice(0, -1).join(', ');
    }

    // Clean up any extra whitespace
    streetAddress = streetAddress.trim();
    city = city.trim();

    return { streetAddress, city, postcode };
  };

  // Survey date is now set via default value in schema, no need for useEffect

  useEffect(() => {
    const currentPanels = form.getValues('panels') || [];
    const legacyPanelDescription = form.getValues('panelsDescription');
    const legacyPanelQuantity = form.getValues('panelsQuantity');

    if (currentPanels.length === 0 && (legacyPanelDescription || legacyPanelQuantity)) {
      form.setValue('panels', [{
        id: 'legacy',
        description: legacyPanelDescription || '',
        quantity: legacyPanelQuantity || 0,
        highlighted: highlightedFields['panelsDescription'] || false,
      }], {
        shouldValidate: false,
        shouldDirty: false,
      });
    }
  }, []);

  // Update panel dimensions when panel type is selected
  useEffect(() => {
    if (selectedPanelType && equipmentOptions?.panels) {
      // Find the selected panel in equipment options
      const selectedPanel = equipmentOptions.panels.find((panel: any) => panel.value === selectedPanelType);

      if (selectedPanel?.specifications) {
        // Update dimensions and electrical specs from equipment specifications
        const newDimensions = {
          width: selectedPanel.specifications.width || panelDimensions.width,
          length: selectedPanel.specifications.length || panelDimensions.length,
          depth: selectedPanel.specifications.depth || 30,
          unit: selectedPanel.specifications.unit || 'mm',
          voc: selectedPanel.specifications.voc,
          isc: selectedPanel.specifications.isc
        };
        setPanelDimensions(newDimensions);
        form.setValue('panelDimensions', newDimensions);
      }
    }
  }, [selectedPanelType, equipmentOptions]);

  // Validate postcode format
  const validatePostcode = (value: string) => {
    if (!value) return true; // Optional field
    return UK_POSTCODE_REGEX.test(value) || 'Please enter a valid UK postcode';
  };

  // Detect if device is mobile
  const isMobileDevice = () => {
    return (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      (navigator.maxTouchPoints && navigator.maxTouchPoints > 2)
    );
  };

  const handleImageUpload = async (field: string, file: File) => {
    setUploadingImages((prev) => ({ ...prev, [field]: true }));

    try {
      const surveyId = form.getValues('surveyId') || existingSurvey?.id;

      await uploadSiteSurveyPhoto({
        form,
        field,
        file,
        surveyId,
        installationId: propInstallationId || installationId,
        customerId,
        description: `Project ${field} photo`,
        toast,
        onPhotoUploaded,
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload Failed',
        description:
          error instanceof Error ? error.message : 'Failed to upload image. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setUploadingImages((prev) => ({ ...prev, [field]: false }));
    }
  };

  const handleFileSelect = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleImageUpload(field, file);
    }
    event.target.value = '';
  };

  const handleCameraCapture = (field: string) => {
    const input = fileInputRefs[field as keyof typeof fileInputRefs];
    if (input.current) {
      if (isMobileDevice()) {
        input.current.setAttribute('capture', 'environment');
      } else {
        input.current.removeAttribute('capture');
      }
      input.current.click();
    }
  };

  const triggerFileUpload = (field: string) => {
    const input = fileInputRefs[field as keyof typeof fileInputRefs];
    if (input.current) {
      input.current.removeAttribute('capture');
      input.current.click();
    }
  };

  const renderImageUpload = (
    fieldName: string,
    label: string,
    description?: string,
    acceptPdf: boolean = false
  ) => {
    return (
      <ResponsiveImageUpload
        form={form}
        fieldName={fieldName}
        label={label}
        description={description}
        isUploading={uploadingImages[fieldName]}
        onCameraCapture={() => handleCameraCapture(fieldName)}
        onFileUpload={() => triggerFileUpload(fieldName)}
        onPhotoDeleted={() => {
          // Trigger photo uploaded callback to initiate auto-save
          onPhotoUploaded?.();
        }}
        acceptPdf={acceptPdf}
      />
    );
  };

  return (
    <div className="space-y-6">
      {/* Hidden file inputs */}
      {Object.entries(fileInputRefs).map(([key, ref]) => (
        <input
          key={key}
          ref={ref}
          type="file"
          accept="image/*,.heic,.heif"
          style={{ display: 'none' }}
          onChange={handleFileSelect(key)}
        />
      ))}

      {/* Survey Information */}
      <Card>
        <CardHeader>
          <CardTitle>Survey Information</CardTitle>
          <CardDescription>Basic information about the site survey</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="siteAssessorName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Surveyor Name</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || ''}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select surveyor" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Peter Seddon">Peter Seddon</SelectItem>
                      <SelectItem value="Dale Pritchard">Dale Pritchard</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="surveyDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Survey Date</FormLabel>
                  <FormControl>
                    <DatePicker
                      date={field.value || new Date().toISOString().split('T')[0]}
                      setDate={(date) => {
                        // Convert date to ISO string format (YYYY-MM-DD)
                        field.onChange(
                          date
                            ? date.toISOString().split('T')[0]
                            : new Date().toISOString().split('T')[0]
                        );
                      }}
                      placeholder="Select survey date"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Check Availability Button */}
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => setShowAvailabilityPopup(true)}
          >
            <CalendarCheck className="h-4 w-4 mr-2" />
            Check Surveyor Availability & Book
          </Button>
        </CardContent>
      </Card>

      {/* Surveyor Availability Popup */}
      <SurveyorAvailabilityPopup
        open={showAvailabilityPopup}
        onOpenChange={setShowAvailabilityPopup}
        initialSurveyor={form.watch('siteAssessorName')}
        initialDate={form.watch('surveyDate') ? new Date(form.watch('surveyDate')) : undefined}
        onSlotSelect={(date, surveyorName) => {
          // Update both the surveyor and date fields
          form.setValue('siteAssessorName', surveyorName);
          form.setValue('surveyDate', date.toISOString().split('T')[0]);
          toast({
            title: 'Survey Booked',
            description: `Scheduled with ${surveyorName} on ${date.toLocaleDateString()} at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
          });
        }}
      />

      {/* Client Information */}
      <Card>
        <CardHeader>
          <CardTitle>Client Information</CardTitle>
          <CardDescription>Customer contact details and installation address</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="clientName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Client Name</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value || ''} placeholder="Full name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="clientTelephone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Phone className="inline h-3 w-3 mr-1" />
                    Telephone Number
                  </FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value || ''} placeholder="+44 7XXX XXXXXX" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="clientEmail"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Mail className="inline h-3 w-3 mr-1" />
                  Email Address
                </FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    {...field}
                    value={field.value || ''}
                    placeholder="client@example.com"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Address Fields - Separated */}
          <div className="space-y-4 border-t pt-4">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Installation Address
            </h4>

            <FormField
              control={form.control}
              name="clientAddress"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Street Address</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value || ''}
                      placeholder="123 Example Street&#10;Apartment 4B"
                      rows={2}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="clientCity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>City/Town</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value || ''} placeholder="London" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="clientPostCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Post Code</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value || ''}
                        placeholder="SW1A 1AA"
                        className="uppercase"
                        onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                rules={{ validate: validatePostcode }}
              />
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/50">
              <div className="flex items-center gap-2 text-sm font-medium">
                <MapPin className="h-4 w-4 text-red-500" />
                what3words precise location
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Automatically loaded from the customer record for surveyors and installers.
              </p>

              {what3wordsDisplayValue && what3wordsMapUrl ? (
                <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
                  <code className="min-w-0 flex-1 rounded bg-white px-3 py-2 font-mono text-sm font-medium text-slate-800 shadow-sm dark:bg-slate-800 dark:text-slate-100">
                    {what3wordsDisplayValue}
                  </code>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={copyWhat3wordsAddress}
                    >
                      {copiedWhat3wordsValue === what3wordsDisplayValue ? (
                        <CheckCheck className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                      {copiedWhat3wordsValue === what3wordsDisplayValue ? 'Copied' : 'Copy'}
                    </Button>
                    <Button asChild variant="outline" size="sm">
                      <a href={what3wordsMapUrl} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4" />
                        Open location
                      </a>
                    </Button>
                  </div>
                </div>
              ) : (
                <Alert className="mt-3 border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                  <AlertDescription>
                    No what3words location has been added for this customer.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Project Details */}
      <Card>
        <CardHeader>
          <CardTitle>System Components</CardTitle>
          <CardDescription>Solar panels, inverters, and battery configuration</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Panels Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">Solar Panels</h4>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addPanel}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Additional Panel
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="panelsDescription"
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>Panel Type</FormLabel>
                    <FormControl>
                      <AddableSelect
                        value={field.value}
                        onChange={(value) => {
                          field.onChange(value);
                          syncPanelEntries(
                            additionalPanels,
                            value,
                            form.getValues('panelsQuantity'),
                            highlightedFields['panelsDescription'] || false
                          );
                        }}
                        placeholder="Select or add panel type"
                        options={equipmentOptions ? equipmentOptions.panels : EQUIPMENT_OPTIONS.panels}
                        equipmentType="panel"
                        isHighlighted={highlightedFields['panelsDescription'] || false}
                        onHighlightChange={(highlighted) => {
                          setHighlightedFields(prev => ({ ...prev, panelsDescription: highlighted }));
                          const currentHighlights = form.getValues('highlightedFields') || {};
                          form.setValue('highlightedFields', { ...currentHighlights, panelsDescription: highlighted });
                          syncPanelEntries(
                            additionalPanels,
                            form.getValues('panelsDescription') || '',
                            form.getValues('panelsQuantity'),
                            highlighted
                          );
                        }}
                        onOptionAdded={() => refetchOptions()}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="panelsQuantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Quantity</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) => {
                          const value = e.target.value === '' ? null : parseInt(e.target.value);
                          field.onChange(value);
                          syncPanelEntries(
                            additionalPanels,
                            form.getValues('panelsDescription') || '',
                            value,
                            highlightedFields['panelsDescription'] || false
                          );
                        }}
                        placeholder="0"
                        min="0"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {additionalPanels.length > 0 && (
              <div className="space-y-3">
                {additionalPanels.map((panel: any, index: number) => (
                  <Card key={panel.id || index}>
                    <CardContent className="pt-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-2">
                          <Label>Panel Type</Label>
                          <AddableSelect
                            value={panel.description}
                            onChange={(value) => updatePanel(index, 'description', value)}
                            placeholder="Select or add panel type"
                            options={equipmentOptions ? equipmentOptions.panels : EQUIPMENT_OPTIONS.panels}
                            equipmentType="panel"
                            isHighlighted={panel.highlighted || false}
                            onHighlightChange={(highlighted) =>
                              updatePanel(index, 'highlighted', highlighted)
                            }
                            onOptionAdded={() => refetchOptions()}
                          />
                        </div>

                        <div className="flex gap-2">
                          <div className="flex-1">
                            <Label>Quantity</Label>
                            <Input
                              type="number"
                              value={panel.quantity || 1}
                              onChange={(e) => updatePanel(index, 'quantity', parseInt(e.target.value) || 1)}
                              placeholder="1"
                              min="1"
                            />
                          </div>
                          <div className="flex items-end">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removePanel(index)}
                              className="text-destructive hover:text-destructive"
                              title="Remove panel"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Panel Dimensions & Electrical Specs - Show when a panel is selected */}
            {selectedPanelType && (
              <div className="space-y-2 mt-4">
                <Label className="text-sm font-medium">Panel Specifications</Label>
                <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                  <div className="space-y-1">
                    <Label htmlFor="panel-width" className="text-xs text-muted-foreground">
                      Width (mm)
                    </Label>
                    <Input
                      id="panel-width"
                      type="number"
                      value={panelDimensions.width || ''}
                      onChange={(e) => {
                        const newDimensions = {
                          ...panelDimensions,
                          width: e.target.value ? parseInt(e.target.value) : undefined
                        };
                        setPanelDimensions(newDimensions);
                        form.setValue('panelDimensions', newDimensions);
                      }}
                      placeholder="Width"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="panel-length" className="text-xs text-muted-foreground">
                      Length (mm)
                    </Label>
                    <Input
                      id="panel-length"
                      type="number"
                      value={panelDimensions.length || ''}
                      onChange={(e) => {
                        const newDimensions = {
                          ...panelDimensions,
                          length: e.target.value ? parseInt(e.target.value) : undefined
                        };
                        setPanelDimensions(newDimensions);
                        form.setValue('panelDimensions', newDimensions);
                      }}
                      placeholder="Length"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="panel-depth" className="text-xs text-muted-foreground">
                      Depth (mm)
                    </Label>
                    <Input
                      id="panel-depth"
                      type="number"
                      value={panelDimensions.depth || 30}
                      onChange={(e) => {
                        const newDimensions = {
                          ...panelDimensions,
                          depth: e.target.value ? parseInt(e.target.value) : 30
                        };
                        setPanelDimensions(newDimensions);
                        form.setValue('panelDimensions', newDimensions);
                      }}
                      placeholder="30"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="panel-voc" className="text-xs text-muted-foreground">
                      Voc (V)
                    </Label>
                    <Input
                      id="panel-voc"
                      type="number"
                      step="0.01"
                      value={panelDimensions.voc || ''}
                      onChange={(e) => {
                        const newDimensions = {
                          ...panelDimensions,
                          voc: e.target.value ? parseFloat(e.target.value) : undefined
                        };
                        setPanelDimensions(newDimensions);
                        form.setValue('panelDimensions', newDimensions);
                      }}
                      placeholder="Voc"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="panel-isc" className="text-xs text-muted-foreground">
                      Isc (A)
                    </Label>
                    <Input
                      id="panel-isc"
                      type="number"
                      step="0.01"
                      value={panelDimensions.isc || ''}
                      onChange={(e) => {
                        const newDimensions = {
                          ...panelDimensions,
                          isc: e.target.value ? parseFloat(e.target.value) : undefined
                        };
                        setPanelDimensions(newDimensions);
                        form.setValue('panelDimensions', newDimensions);
                      }}
                      placeholder="Isc"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Specifications auto-populated from panel type. You can adjust them if needed.
                </p>
              </div>
            )}
          </div>

          {/* Inverters Section */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium">Inverters</h4>
            <div className="space-y-3">
              {[1, 2, 3].map((num) => {
                const fieldName = `inverter${num}`;
                return (
                  <FormField
                    key={num}
                    control={form.control}
                    name={fieldName}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Inverter {num}{' '}
                          {num === 1 && <span className="text-gray-500">(Primary)</span>}
                        </FormLabel>
                        <FormControl>
                          <AddableSelect
                            value={field.value}
                            onChange={field.onChange}
                            placeholder={`Select or add inverter ${num > 1 ? '(optional)' : ''}`}
                            equipmentType="inverter"
                            isHighlighted={highlightedFields[fieldName] || false}
                            onHighlightChange={(highlighted) => {
                              setHighlightedFields(prev => ({ ...prev, [fieldName]: highlighted }));
                              const currentHighlights = form.getValues('highlightedFields') || {};
                              form.setValue('highlightedFields', { ...currentHighlights, [fieldName]: highlighted });
                            }}
                            options={equipmentOptions ? formatEquipmentOptionsForSelect(equipmentOptions, 'inverter') : [
                              {
                                label: 'Sigenergy - Single Phase',
                                options: EQUIPMENT_OPTIONS.inverters.sigenergySinglePhase
                              },
                              {
                                label: 'Sigenergy - Three Phase',
                                options: EQUIPMENT_OPTIONS.inverters.sigenergyThreePhase
                              },
                              {
                                label: 'Hanchu - Single Phase',
                                options: EQUIPMENT_OPTIONS.inverters.hanchuSinglePhase
                              }
                            ]}
                            onOptionAdded={() => refetchOptions()}
                          />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                );
              })}
            </div>
          </div>

          {/* Battery Section - Dynamic Entries */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">Battery Storage</h4>
              {batteries.length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addBattery}
                >
                  Add Additional Battery
                </Button>
              )}
            </div>

            {batteries.length === 0 ? (
              // Empty State
              <div className="border-2 border-dashed rounded-lg p-8 text-center">
                <p className="text-sm text-muted-foreground mb-4">
                  No batteries added yet
                </p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={addBattery}
                >
                  Add Battery
                </Button>
              </div>
            ) : (
              // Battery Entries
              <div className="space-y-3">
                {batteries.map((battery: any, index: number) => (
                  <Card key={battery.id}>
                    <CardContent className="pt-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-2">
                          <Label>Battery Type</Label>
                          <AddableSelect
                            value={battery.description}
                            onChange={(value) => updateBattery(index, 'description', value)}
                            placeholder="Select or add battery"
                            equipmentType="battery"
                            isHighlighted={battery.highlighted || false}
                            onHighlightChange={(highlighted) =>
                              updateBattery(index, 'highlighted', highlighted)
                            }
                            options={equipmentOptions ? formatEquipmentOptionsForSelect(equipmentOptions, 'battery') : [
                              { label: 'Sigen Batteries', options: EQUIPMENT_OPTIONS.batteries.sigen },
                              { label: 'Hanchu Batteries', options: EQUIPMENT_OPTIONS.batteries.hanchu }
                            ]}
                            onOptionAdded={() => refetchOptions()}
                          />
                        </div>

                        <div className="flex gap-2">
                          <div className="flex-1">
                            <Label>Quantity</Label>
                            <Input
                              type="number"
                              value={battery.quantity || 1}
                              onChange={(e) => updateBattery(index, 'quantity', parseInt(e.target.value) || 1)}
                              placeholder="1"
                              min="1"
                            />
                          </div>
                          <div className="flex items-end">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeBattery(index)}
                              className="text-destructive hover:text-destructive"
                              title="Remove battery"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Property Details */}
      <Card>
        <CardHeader>
          <CardTitle>Property Details</CardTitle>
          <CardDescription>Property type and project photos</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <FormField
            control={form.control}
            name="propertyType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Property Type</FormLabel>
                <FormControl>
                  <HighlightableSelect
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select property type"
                    isHighlighted={highlightedFields['propertyType'] || false}
                    onHighlightChange={(highlighted) => {
                      setHighlightedFields(prev => ({ ...prev, propertyType: highlighted }));
                      const currentHighlights = form.getValues('highlightedFields') || {};
                      form.setValue('highlightedFields', { ...currentHighlights, propertyType: highlighted });
                    }}
                    options={[
                      { value: "HOUSE", label: "Detached House" },
                      { value: "SEMI", label: "Semi-Detached House" },
                      { value: "TERRACED", label: "Terraced House" },
                      { value: "BUNGALOW", label: "Bungalow" },
                      { value: "FLAT", label: "Flat/Apartment" },
                      { value: "OUTBUILDING", label: "Outbuilding" },
                      { value: "INDUSTRIAL", label: "Industrial Building" },
                      { value: "OFFICES", label: "Office Building" },
                      { value: "OTHER", label: "Other Commercial" }
                    ]}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Project Photos */}
          <div className="space-y-6">
            <h4 className="text-sm font-medium">Project Documentation Photos</h4>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {renderImageUpload(
                'stringPlanFromOpenSolar',
                'String Plan',
                'Upload the string plan diagram from OpenSolar'
              )}
              {renderImageUpload(
                'aerialImageFromDrone',
                'Aerial View',
                'Drone or satellite image showing roof layout'
              )}
            </div>

            <div className="space-y-4">
              <h5 className="text-sm text-gray-600 font-medium">General Site Photos</h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {renderImageUpload('generalImage1', 'Site Photo 1')}
                {renderImageUpload('generalImage2', 'Site Photo 2')}
                {renderImageUpload('generalImage3', 'Site Photo 3')}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
